// Progress for guests and signed-in learners.
// Guests: the server grades every answer and returns signed receipts, kept in localStorage; XP is the sum of
// verified receipts and is migrated (without duplicates) when the guest signs up. Signed-in: the server's tables.

import type { ReactNode } from 'react'
import type { LessonRecord, MasteryRecord, ProgressSnapshot } from '@/prakrit/lib/progress-shared'

import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react'
import { useSession } from '@/lib/client/session'
import { levelFromXp, streakFromDays } from '@/prakrit/lib/progress-shared'
import { browserTimeZone, dayIn } from '@/lib/time-zone'

export type { LessonRecord, MasteryRecord, ProgressSnapshot } from '@/prakrit/lib/progress-shared'

export interface ReviewEntry {
    ref: string
    form: string
    note?: string
    addedAt: string
}

interface Receipt {
    t: 'xp' | 'lesson'
    src: string
    amt: number
    day: string
    lesson?: string
    score?: number
}

interface GuestStore {
    v: 1
    receipts: string[]
    started: string[]
    recent: string[]
    review: ReviewEntry[]
    mastery: Record<string, MasteryRecord>
    errors: Record<string, number>
}

const STORE_KEY = 'vaani:guest-progress'

const EMPTY_STORE: GuestStore = { v: 1, receipts: [], started: [], recent: [], review: [], mastery: {}, errors: {} }

const decodeReceipt = (token: string): Receipt | null => {
    try {
        const [body] = token.split('.')
        const json = atob(body.replace(/-/g, '+').replace(/_/g, '/'))
        const bytes = Uint8Array.from(json, c => c.charCodeAt(0))

        return JSON.parse(new TextDecoder().decode(bytes)) as Receipt
    }
    catch {
        return null
    }
}

/** Today on this device (the learning day follows local midnight). */
const todayLocal = () => dayIn(browserTimeZone())

const fromGuest = (store: GuestStore): ProgressSnapshot => {
    const seen = new Set<string>()
    const lessons: Record<string, LessonRecord> = {}
    const days = new Set<string>()
    let xpTotal = 0
    let xpToday = 0
    const today = todayLocal()

    for (const id of store.started) lessons[id] = { status: 'started', best: 0 }

    for (const token of store.receipts) {
        const r = decodeReceipt(token)

        if (!r) continue

        const dedupe = `${r.src}|${r.day}`

        if (seen.has(dedupe)) continue

        seen.add(dedupe)

        // Lesson receipts carry no XP (XP is counted on accounts only), so they never count as an XP day.
        if (r.amt > 0) {
            xpTotal += r.amt
            days.add(r.day)

            if (r.day === today) xpToday += r.amt
        }

        if (r.t === 'lesson' && r.lesson) {
            const prev = lessons[r.lesson]
            const best = Math.max(prev?.best ?? 0, r.score ?? 0)

            lessons[r.lesson] = { status: 'completed', best, completedAt: prev?.completedAt ?? r.day }
        }
    }

    const { level, floor, next } = levelFromXp(xpTotal)
    const dayList = Array.from(days).sort()

    return {
        lessons,
        xpTotal,
        xpToday,
        days: dayList,
        // Guest days come from this device: the streak follows its own midnight.
        streak: streakFromDays(dayList, dayIn(browserTimeZone())),
        level,
        levelFloor: floor,
        levelNext: next,
        mastery: Object.values(store.mastery),
        errors: Object.entries(store.errors).map(([code, count]) => ({ code, count })).sort((a, b) => b.count - a.count),
        badges: []
    }
}

export interface GradeEffects {
    receipts?: string[]
    xpAwarded?: number
    attempts?: { ref: string, skill: string, correct: boolean, code?: string, label?: string }[]
}

interface ProgressContextValue {
    ready: boolean
    snapshot: ProgressSnapshot
    recent: string[]
    review: ReviewEntry[]
    signedIn: boolean
    apply: (effects: GradeEffects) => void
    markStarted: (lessonId: string) => void
    addRecent: (form: string) => void
    addToReview: (entry: Omit<ReviewEntry, 'addedAt'>) => boolean
    removeFromReview: (ref: string) => void
    reload: () => Promise<void>
}

const ProgressContext = createContext<ProgressContextValue | null>(null)

const nextMastery = (prev: MasteryRecord | undefined, ref: string, skill: string, correct: boolean, label?: string): MasteryRecord => {
    const base: MasteryRecord = prev ?? { ref, skill, state: 'new', correct: 0, wrong: 0, dueAt: null, label }
    const c = base.correct + (correct ? 1 : 0)
    const w = base.wrong + (correct ? 0 : 1)
    let state: MasteryRecord['state'] = 'learning'

    if (!correct && w >= 2 && w >= c) state = 'needs_practice'
    else if (c >= 6 && w <= c / 4) state = 'mastered'
    else if (c >= 3 && w <= c / 2) state = 'strong'

    const intervalDays = state === 'mastered' ? 14 : state === 'strong' ? 4 : state === 'needs_practice' ? 0.5 : 1
    const due = new Date(Date.now() + intervalDays * 86400000).toISOString()

    return { ...base, label: label ?? base.label, correct: c, wrong: w, state, dueAt: due }
}

export const ProgressProvider = ({ children }: { children: ReactNode }): ReactNode => {
    const { user, loading } = useSession()
    const [store, setStore] = useState<GuestStore>(EMPTY_STORE)
    const [server, setServer] = useState<ProgressSnapshot | null>(null)
    const [ready, setReady] = useState(false)
    const storeRef = useRef<GuestStore>(EMPTY_STORE)
    const migrated = useRef<string | null>(null)
    const migrating = useRef<string | null>(null)

    const save = useCallback((next: GuestStore) => {
        storeRef.current = next
        setStore(next)

        try {
            localStorage.setItem(STORE_KEY, JSON.stringify(next))
        }
        catch {
            // Storage full or blocked: progress still counts for this visit.
        }
    }, [])

    useEffect(() => {
        try {
            const raw = localStorage.getItem(STORE_KEY)

            if (raw) {
                const parsed = { ...EMPTY_STORE, ...(JSON.parse(raw) as Partial<GuestStore>) } as GuestStore

                storeRef.current = parsed
                setStore(parsed)
            }
        }
        catch {
            // Corrupt storage: start fresh.
        }
    }, [])

    const reload = useCallback(async () => {
        if (!user) return

        try {
            const res = await fetch('/api/progress', { cache: 'no-store' })

            if (res.ok) setServer(await res.json() as ProgressSnapshot)
        }
        catch {
            // Offline: keep what we have.
        }
    }, [user])

    const retryTimer = useRef<number | null>(null)

    /**
     * Import this browser's guest progress into the account. Marked done only once the server confirms; the import is
     * safe to repeat (XP counts once per source and day, lessons keep their best score). 'done' when nothing is left,
     * 'retry' after a server or network failure, 'stop' when the server refused it or an import is already running.
     */
    const importGuest = useCallback(async (userId: string): Promise<'done' | 'retry' | 'stop'> => {
        const guest = storeRef.current

        if (migrated.current === userId || (guest.receipts.length === 0 && Object.keys(guest.mastery).length === 0)) return 'done'
        if (migrating.current === userId) return 'stop'

        migrating.current = userId

        try {
            const res = await fetch('/api/progress/migrate', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ receipts: guest.receipts, mastery: Object.values(guest.mastery) })
            })

            if (res.ok) {
                migrated.current = userId
                save({ ...storeRef.current, receipts: [], mastery: {}, errors: {} })

                return 'done'
            }

            console.error('[progress] guest progress was not imported', res.status)

            // A server or network hiccup is tried again; a refused import (bad data, signed out) is not.
            return res.status >= 500 || res.status === 429 ? 'retry' : 'stop'
        }
        catch (error) {
            console.error('[progress] guest progress could not be imported', error instanceof Error ? error.message : error)

            return 'retry'
        }
        finally {
            if (migrating.current === userId) migrating.current = null
        }
    }, [save])

    // Signed in: import guest progress (retrying after 15 s, 1 min and 5 min, and when the connection returns), then
    // load the account's progress.
    useEffect(() => {
        if (loading) return

        if (!user) {
            setServer(null)
            setReady(true)

            return
        }

        const userId = user.id
        let cancelled = false
        const delays = [15000, 60000, 300000]

        const attempt = async (n: number) => {
            const outcome = await importGuest(userId)

            if (cancelled) return

            if (outcome === 'done' && n > 0) await reload()

            if (outcome === 'retry' && n < delays.length) {
                retryTimer.current = window.setTimeout(() => { void attempt(n + 1) }, delays[n])
            }
        }

        const onOnline = () => {
            if (retryTimer.current) window.clearTimeout(retryTimer.current)
            void attempt(1)
        }

        const run = async () => {
            await attempt(0)

            if (cancelled) return

            await reload()
            setReady(true)
        }

        window.addEventListener('online', onOnline)
        run()

        return () => {
            cancelled = true
            window.removeEventListener('online', onOnline)

            if (retryTimer.current) window.clearTimeout(retryTimer.current)
        }
    }, [user, loading, reload, importGuest])

    const apply = useCallback((effects: GradeEffects) => {
        if (user) {
            reload()

            return
        }

        const current = storeRef.current
        const mastery = { ...current.mastery }
        const errors = { ...current.errors }

        for (const a of effects.attempts ?? []) {
            mastery[a.ref] = nextMastery(mastery[a.ref], a.ref, a.skill, a.correct, a.label)

            if (!a.correct && a.code) errors[a.code] = (errors[a.code] ?? 0) + 1
        }

        save({ ...current, receipts: [...current.receipts, ...(effects.receipts ?? [])], mastery, errors })
    }, [user, reload, save])

    const markStarted = useCallback((lessonId: string) => {
        const current = storeRef.current

        if (current.started.includes(lessonId)) return

        save({ ...current, started: [...current.started, lessonId] })
    }, [save])

    const addRecent = useCallback((form: string) => {
        const current = storeRef.current
        const recent = [form, ...current.recent.filter(f => f !== form)].slice(0, 24)

        save({ ...current, recent })
    }, [save])

    const addToReview = useCallback((entry: Omit<ReviewEntry, 'addedAt'>) => {
        const current = storeRef.current

        if (current.review.some(r => r.ref === entry.ref)) return false

        save({ ...current, review: [{ ...entry, addedAt: new Date().toISOString() }, ...current.review].slice(0, 200) })

        return true
    }, [save])

    const removeFromReview = useCallback((ref: string) => {
        const current = storeRef.current

        save({ ...current, review: current.review.filter(r => r.ref !== ref) })
    }, [save])

    const snapshot = useMemo(() => {
        const guest = fromGuest(store)

        if (!server) return guest

        // Lessons started on this device but not yet graded still show as started.
        const lessons = { ...guest.lessons, ...server.lessons }

        return { ...server, lessons }
    }, [store, server])

    const value = useMemo<ProgressContextValue>(() => ({
        ready,
        snapshot,
        recent: store.recent,
        review: store.review,
        signedIn: Boolean(user),
        apply,
        markStarted,
        addRecent,
        addToReview,
        removeFromReview,
        reload
    }), [ready, snapshot, store.recent, store.review, user, apply, markStarted, addRecent, addToReview, removeFromReview, reload])

    return <ProgressContext.Provider value={value}>{children}</ProgressContext.Provider>
}

export const useProgress = (): ProgressContextValue => {
    const ctx = useContext(ProgressContext)

    if (!ctx) throw new Error('useProgress must be used inside ProgressProvider')

    return ctx
}
